'use client';

/**
 * Tier P5.9 — Generic progress-indicator primitive.
 *
 * Three composable modes, one consistent skin:
 *
 *   1. **stepped** — named step list with check / pulse / number glyphs,
 *      a % bar, an optional rotating "reasoning" ticker. Modelled on the
 *      original CopilotPipeline used in `/assistant`.
 *
 *   2. **ticker** — elapsed-seconds counter + a stage label that swaps
 *      out as time passes (long-form template draft pattern). No step
 *      list, no explicit %.
 *
 *   3. **spinner** — minimal one-liner: pulse dot + label + optional
 *      sub-text. Use for short-running operations where a multi-step
 *      pipeline would overstate progress.
 *
 * Tone is configurable (violet · emerald · sky · amber) so the same
 * primitive can ship on contrast-different surfaces (research panel,
 * copilot ask, long drafts, file uploads).
 */
import { useEffect, useState } from 'react';

import { cn } from './cn';

export interface ProgressStep {
  id: string | number;
  label: string;
}

export type ProgressTone = 'violet' | 'emerald' | 'sky' | 'amber';

interface BaseProps {
  title?: string;
  subtitle?: string;
  tone?: ProgressTone;
  /** Override the outer wrapper classes (default: white card). */
  className?: string;
  /** Optional rotating sub-text (italic, footer slot). */
  tickerLines?: readonly string[];
  /** Footer text rendered below the body (no ticker). */
  footer?: string;
}

interface SteppedProps extends BaseProps {
  mode: 'stepped';
  steps: readonly ProgressStep[];
  /** 0-based index of the currently in-progress step. */
  currentStep: number;
  /** 0-100; controls the progress bar. */
  pct: number;
  /** Right-of-title slot — accepts node so callers can render badges. */
  trailing?: React.ReactNode;
}

interface TickerProps extends BaseProps {
  mode: 'ticker';
  /** Seconds elapsed — shown in the right-corner chip. */
  elapsedSeconds: number;
  /** Stage label rotating over time — caller picks based on elapsed. */
  stage: string;
  /** Indeterminate pulse bar; explicit `pct` shows a determinate bar. */
  pct?: number;
}

interface SpinnerProps extends BaseProps {
  mode: 'spinner';
  /** Single line under the title — already styled italic + muted. */
  hint?: string;
}

type Props = SteppedProps | TickerProps | SpinnerProps;

const TONE: Record<
  ProgressTone,
  {
    bar: string;
    barTrack: string;
    headDot: string;
    active: string;
    activeRingDot: string;
    cardBorder: string;
    cardBg: string;
    titleText: string;
    metaChip: string;
    tickerDot: string;
  }
> = {
  violet: {
    bar: 'bg-gradient-to-r from-violet-500 to-brand-primary-500',
    barTrack: 'bg-slate-100',
    headDot: 'bg-violet-400',
    active: 'bg-violet-100',
    activeRingDot: 'bg-violet-500',
    cardBorder: 'border-slate-200',
    cardBg: 'bg-white',
    titleText: 'text-slate-800',
    metaChip: 'bg-violet-100 text-violet-900',
    tickerDot: 'bg-violet-500',
  },
  emerald: {
    bar: 'bg-gradient-to-r from-emerald-500 to-teal-500',
    barTrack: 'bg-slate-100',
    headDot: 'bg-emerald-400',
    active: 'bg-emerald-100',
    activeRingDot: 'bg-emerald-500',
    cardBorder: 'border-emerald-200',
    cardBg: 'bg-white',
    titleText: 'text-slate-800',
    metaChip: 'bg-emerald-100 text-emerald-900',
    tickerDot: 'bg-emerald-500',
  },
  sky: {
    bar: 'bg-gradient-to-r from-sky-500 to-indigo-500',
    barTrack: 'bg-slate-100',
    headDot: 'bg-sky-400',
    active: 'bg-sky-100',
    activeRingDot: 'bg-sky-500',
    cardBorder: 'border-sky-200',
    cardBg: 'bg-white',
    titleText: 'text-slate-800',
    metaChip: 'bg-sky-100 text-sky-900',
    tickerDot: 'bg-sky-500',
  },
  amber: {
    bar: 'bg-gradient-to-r from-amber-500 to-orange-500',
    barTrack: 'bg-slate-100',
    headDot: 'bg-amber-400',
    active: 'bg-amber-100',
    activeRingDot: 'bg-amber-500',
    cardBorder: 'border-amber-200',
    cardBg: 'bg-white',
    titleText: 'text-slate-800',
    metaChip: 'bg-amber-100 text-amber-900',
    tickerDot: 'bg-amber-500',
  },
};

export function ProgressIndicator(props: Props) {
  const tone = TONE[props.tone ?? 'violet'];
  const card = cn(
    'rounded-lg border p-4 shadow-sm',
    tone.cardBorder,
    tone.cardBg,
    props.className,
  );

  if (props.mode === 'spinner') {
    return (
      <div aria-live="polite" className={card} role="status">
        <div className="flex items-center gap-2">
          <span className={cn('inline-block h-2 w-2 animate-pulse rounded-full', tone.tickerDot)} />
          <div className="min-w-0 flex-1">
            <p className={cn('text-sm font-medium', tone.titleText)}>
              {props.title ?? 'Working'}
              <DotPulse toneClass={tone.headDot} />
            </p>
            {props.hint ? (
              <p className="mt-0.5 truncate text-[11px] italic text-slate-500">
                {props.hint}
              </p>
            ) : null}
          </div>
        </div>
        {props.tickerLines && props.tickerLines.length > 0 ? (
          <ReasoningTicker
            dotClass={tone.tickerDot}
            lines={props.tickerLines}
          />
        ) : null}
        {props.footer ? (
          <p className="mt-2 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
            {props.footer}
          </p>
        ) : null}
      </div>
    );
  }

  if (props.mode === 'ticker') {
    const explicitPct = typeof props.pct === 'number';
    return (
      <div aria-live="polite" className={card} role="status">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-block h-2 w-2 animate-pulse rounded-full',
              tone.tickerDot,
            )}
          />
          <p className={cn('text-sm font-semibold', tone.titleText)}>
            {props.title ?? 'Working'}
          </p>
          <span
            className={cn(
              'ml-auto rounded px-1.5 py-0.5 font-mono text-[10px] tabular-nums',
              tone.metaChip,
            )}
          >
            {props.elapsedSeconds}s
          </span>
        </div>
        <p className="mt-1.5 text-[11.5px] text-slate-700">{props.stage}</p>
        <div className={cn('mt-2 h-0.5 w-full overflow-hidden rounded', tone.barTrack)}>
          <div
            className={cn(
              'h-full rounded',
              tone.bar,
              !explicitPct && 'w-full animate-pulse opacity-70',
              explicitPct && 'transition-[width] duration-500 ease-out',
            )}
            style={explicitPct ? { width: `${props.pct}%` } : undefined}
          />
        </div>
        {props.tickerLines && props.tickerLines.length > 0 ? (
          <ReasoningTicker
            dotClass={tone.tickerDot}
            lines={props.tickerLines}
          />
        ) : null}
        {props.footer ? (
          <p className="mt-2 text-[10.5px] leading-relaxed text-slate-600">
            {props.footer}
          </p>
        ) : null}
      </div>
    );
  }

  // stepped
  return (
    <div aria-live="polite" className={card} role="status">
      <div className="mb-3 flex items-center justify-between">
        <div className={cn('text-sm font-medium', tone.titleText)}>
          {props.title ?? 'Working'}
          <DotPulse toneClass={tone.headDot} />
        </div>
        <div className="flex items-center gap-2">
          {props.trailing}
          <div className="font-mono text-xs text-slate-500">
            {Math.round(props.pct)}%
          </div>
        </div>
      </div>
      <div className={cn('mb-3 h-1 overflow-hidden rounded-full', tone.barTrack)}>
        <div
          className={cn('h-full transition-[width] duration-500 ease-out', tone.bar)}
          style={{ width: `${props.pct}%` }}
        />
      </div>
      <ol className="space-y-1.5">
        {props.steps.map((s, i) => {
          const state =
            i < props.currentStep
              ? 'done'
              : i === props.currentStep
                ? 'active'
                : 'pending';
          return (
            <li
              key={s.id}
              className="flex items-center gap-2 text-xs"
              data-state={state}
            >
              <span
                className={cn(
                  'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                  state === 'done'
                    ? 'bg-emerald-100 text-emerald-700'
                    : state === 'active'
                      ? tone.active
                      : 'bg-slate-100 text-slate-400',
                )}
              >
                {state === 'done' ? (
                  <svg
                    fill="none"
                    height={9}
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    viewBox="0 0 10 10"
                    width={9}
                  >
                    <path d="M2 5l2 2 4-4.5" />
                  </svg>
                ) : state === 'active' ? (
                  <span
                    className={cn(
                      'h-1.5 w-1.5 animate-ping rounded-full',
                      tone.activeRingDot,
                    )}
                  />
                ) : (
                  <span className="text-[10px] font-medium">{i + 1}</span>
                )}
              </span>
              <span
                className={
                  state === 'done'
                    ? 'text-slate-500 line-through decoration-slate-300'
                    : state === 'active'
                      ? 'font-medium text-slate-900'
                      : 'text-slate-400'
                }
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
      {props.subtitle ? (
        <p className="mt-2 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
          {props.subtitle}
        </p>
      ) : null}
      {props.tickerLines && props.tickerLines.length > 0 ? (
        <ReasoningTicker dotClass={tone.tickerDot} lines={props.tickerLines} />
      ) : null}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function DotPulse({ toneClass }: { toneClass: string }) {
  return (
    <span aria-hidden className="ml-0.5 inline-flex">
      <span
        className={cn(
          'h-1 w-1 animate-bounce rounded-full',
          toneClass,
          '[animation-delay:-0.3s]',
        )}
      />
      <span
        className={cn(
          'ml-0.5 h-1 w-1 animate-bounce rounded-full',
          toneClass,
          '[animation-delay:-0.15s]',
        )}
      />
      <span
        className={cn('ml-0.5 h-1 w-1 animate-bounce rounded-full', toneClass)}
      />
    </span>
  );
}

function ReasoningTicker({
  lines,
  dotClass,
  intervalMs = 1700,
}: {
  lines: readonly string[];
  dotClass: string;
  intervalMs?: number;
}) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (lines.length <= 1) return;
    const id = setInterval(
      () => setI((v) => (v + 1) % lines.length),
      intervalMs,
    );
    return () => clearInterval(id);
  }, [lines.length, intervalMs]);
  return (
    <div className="mt-3 flex items-center gap-1.5 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
      <span className={cn('h-1 w-1 animate-pulse rounded-full', dotClass)} />
      <span key={i} className="italic">
        {lines[i]}
      </span>
    </div>
  );
}
